import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { lastValueFrom, take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  PatrolActivityStat,
  PatrolDiscordBotHealth,
  PatrolDiscordChannelOption,
  PatrolDiscordGuild,
  PatrolDiscordRoleOption,
  PatrolGuildSettings,
  PatrolChannelAccessPolicy,
  PatrolChannelPolicy,
  PatrolChannelAccessMode,
  PatrolModerationCase,
  PatrolModQueueItem,
  PatrolCaseRecord,
  PatrolCaseSummary,
  PatrolModerationLedgerResponse,
  PatrolPolicyPresetId,
  PatrolRolePolicy,
} from '../models/patrol-discord-cockpit.model';

export interface PatrolGuildPoliciesResponse {
  guildSettings: PatrolGuildSettings;
  rolePolicies: PatrolRolePolicy[];
  channelPolicies: PatrolChannelPolicy[];
  channelAccessPolicies: PatrolChannelAccessPolicy[];
  discordRoles: PatrolDiscordRoleOption[];
  discordChannels: PatrolDiscordChannelOption[];
}

export type UpdatePatrolGuildPoliciesRequest = {
  guildSettings?: Partial<PatrolGuildSettings>;
  rolePolicies?: Array<{
    discordRoleId: string;
    policyId: PatrolPolicyPresetId;
  }>;
  channelPolicies?: Array<{
    discordChannelId: string;
    policyId: PatrolPolicyPresetId;
  }>;
  channelAccessPolicies?: Array<{
    discordChannelId: string;
    accessMode: PatrolChannelAccessMode;
    minAge?: number;
    maxAge?: number;
    roleName?: string;
  }>;
};

export type PatrolGuildActivityStatId =
  | 'incidents_30d'
  | 'actions_today'
  | 'linked_members'
  | 'high_severity_7d';

export interface PatrolGuildActivityResponse {
  stats: Array<{
    id: PatrolGuildActivityStatId;
    value: number;
    trendDirection?: 'up' | 'down' | 'neutral';
    trendDelta?: number;
  }>;
  recentCases: Array<{
    caseId: string;
    occurredAt: string;
    channelName: string;
    action: string;
    reason: string;
    reputationDelta?: number;
  }>;
}

export interface PatrolDiscordSetupResponse {
  botInstalled: boolean;
  botReady: boolean;
  guildCount: number;
  loggedInAs: string | null;
  inviteUrl: string | null;
  startupError: string | null;
}

@Injectable({ providedIn: 'root' })
export class PatrolDiscordApiService {
  #http = inject(HttpClient);
  #base = `${environment.baseUrl}/bots/discord/patrol`;

  listGuilds(): Promise<PatrolDiscordGuild[]> {
    return lastValueFrom(
      this.#http.get<PatrolDiscordGuild[]>(`${this.#base}/me/guilds`).pipe(take(1)),
    );
  }

  createGuild(displayName: string): Promise<PatrolDiscordGuild> {
    return lastValueFrom(
      this.#http
        .post<PatrolDiscordGuild>(`${this.#base}/me/guilds`, { displayName })
        .pipe(take(1)),
    );
  }

  completeGuildLink(
    registryId: string,
    communityId: string,
  ): Promise<PatrolDiscordGuild> {
    return lastValueFrom(
      this.#http
        .post<PatrolDiscordGuild>(
          `${this.#base}/me/guilds/${encodeURIComponent(registryId)}/complete-link`,
          { communityId },
        )
        .pipe(take(1)),
    );
  }

  getSetup(): Promise<PatrolDiscordSetupResponse> {
    return lastValueFrom(
      this.#http
        .get<PatrolDiscordSetupResponse>(`${this.#base}/me/setup`)
        .pipe(take(1)),
    );
  }

  getGuildPolicies(guildId: string): Promise<PatrolGuildPoliciesResponse> {
    return lastValueFrom(
      this.#http
        .get<PatrolGuildPoliciesResponse>(
          `${this.#base}/me/guilds/${encodeURIComponent(guildId)}/policies`,
        )
        .pipe(take(1)),
    );
  }

  updateGuildPolicies(
    guildId: string,
    body: UpdatePatrolGuildPoliciesRequest,
  ): Promise<PatrolGuildPoliciesResponse> {
    return lastValueFrom(
      this.#http
        .patch<PatrolGuildPoliciesResponse>(
          `${this.#base}/me/guilds/${encodeURIComponent(guildId)}/policies`,
          body,
        )
        .pipe(take(1)),
    );
  }

  getGuildActivity(guildId: string): Promise<PatrolGuildActivityResponse> {
    return lastValueFrom(
      this.#http
        .get<PatrolGuildActivityResponse>(
          `${this.#base}/me/guilds/${encodeURIComponent(guildId)}/activity`,
        )
        .pipe(take(1)),
    );
  }

  listModQueue(communityId?: string): Promise<PatrolModQueueItem[]> {
    const query = communityId
      ? `?communityId=${encodeURIComponent(communityId)}`
      : '';
    return lastValueFrom(
      this.#http
        .get<PatrolModQueueItem[]>(`${this.#base}/me/mod-queue${query}`)
        .pipe(take(1)),
    );
  }

  approveModQueueItem(id: string): Promise<PatrolModQueueItem> {
    return lastValueFrom(
      this.#http
        .post<PatrolModQueueItem>(
          `${this.#base}/me/mod-queue/${encodeURIComponent(id)}/approve`,
          {},
        )
        .pipe(take(1)),
    );
  }

  rejectModQueueItem(id: string): Promise<PatrolModQueueItem> {
    return lastValueFrom(
      this.#http
        .post<PatrolModQueueItem>(
          `${this.#base}/me/mod-queue/${encodeURIComponent(id)}/reject`,
          {},
        )
        .pipe(take(1)),
    );
  }

  searchCases(
    guildId: string,
    options: { discordUserId?: string; caseIdPrefix?: string; limit?: number },
  ): Promise<PatrolCaseSummary[]> {
    const params = new URLSearchParams();
    if (options.discordUserId) {
      params.set('discordUserId', options.discordUserId);
    }
    if (options.caseIdPrefix) {
      params.set('caseIdPrefix', options.caseIdPrefix);
    }
    if (options.limit != null) {
      params.set('limit', String(options.limit));
    }
    const query = params.toString();
    const suffix = query ? `?${query}` : '';
    return lastValueFrom(
      this.#http
        .get<PatrolCaseSummary[]>(
          `${this.#base}/me/guilds/${encodeURIComponent(guildId)}/cases${suffix}`,
        )
        .pipe(take(1)),
    );
  }

  getCase(
    guildId: string,
    caseId: string,
  ): Promise<PatrolCaseRecord> {
    return lastValueFrom(
      this.#http
        .get<PatrolCaseRecord>(
          `${this.#base}/me/guilds/${encodeURIComponent(guildId)}/cases/${encodeURIComponent(caseId)}`,
        )
        .pipe(take(1)),
    );
  }

  getModerationLedger(
    guildId: string,
    options: {
      discordUserId?: string;
      limit?: number;
      before?: string;
    } = {},
  ): Promise<PatrolModerationLedgerResponse> {
    const params = new URLSearchParams();
    if (options.discordUserId) {
      params.set('discordUserId', options.discordUserId);
    }
    if (options.limit != null) {
      params.set('limit', String(options.limit));
    }
    if (options.before) {
      params.set('before', options.before);
    }
    const query = params.toString();
    const suffix = query ? `?${query}` : '';
    return lastValueFrom(
      this.#http
        .get<PatrolModerationLedgerResponse>(
          `${this.#base}/me/guilds/${encodeURIComponent(guildId)}/moderation-ledger${suffix}`,
        )
        .pipe(take(1)),
    );
  }

  mapSetupToBotHealth(setup: PatrolDiscordSetupResponse): PatrolDiscordBotHealth {
    return {
      ready: setup.botReady,
      loggedInAs: setup.loggedInAs ?? 'Valvur Patrol',
      startupError: setup.startupError ?? undefined,
      inviteUrl: setup.inviteUrl ?? '',
      lastCheckedAt: new Date().toISOString(),
    };
  }

  mapActivityResponse(response: PatrolGuildActivityResponse): {
    stats: PatrolActivityStat[];
    recentCases: PatrolModerationCase[];
  } {
    const I18N = 'app.patrol.discord.cockpit.activity.stats';
    const stats: PatrolActivityStat[] = response.stats.map((stat) => {
      const base = this.#mapActivityStatBase(stat.id, stat.value, I18N);
      if (stat.id === 'actions_today' && stat.trendDelta !== undefined) {
        const delta = stat.trendDelta;
        const sign = delta > 0 ? '+' : '';
        return {
          ...base,
          trendKey: `${I18N}.vsYesterday`,
          trendParams: { delta: `${sign}${delta}` },
          trendDirection: stat.trendDirection,
        };
      }
      if (stat.id === 'linked_members' && stat.trendDelta && stat.trendDelta > 0) {
        return {
          ...base,
          trendKey: `${I18N}.plusThisWeek`,
          trendParams: { count: stat.trendDelta },
          trendDirection: 'up',
        };
      }
      return base;
    });

    const recentCases: PatrolModerationCase[] = response.recentCases.map(
      (item) => ({
        caseId: item.caseId,
        occurredAt: item.occurredAt,
        channelName: item.channelName,
        action: item.action,
        reason: item.reason,
        reputationDelta: item.reputationDelta,
      }),
    );

    return { stats, recentCases };
  }

  #mapActivityStatBase(
    id: PatrolGuildActivityStatId,
    value: number,
    i18n: string,
  ): PatrolActivityStat {
    const labelById: Record<PatrolGuildActivityStatId, string> = {
      incidents_30d: `${i18n}.incidents30d`,
      actions_today: `${i18n}.actionsToday`,
      linked_members: `${i18n}.linkedMembers`,
      high_severity_7d: `${i18n}.highSeverity7d`,
    };

    return {
      id,
      labelKey: labelById[id],
      value: value.toLocaleString(),
    };
  }
}
